import React, { useMemo, useEffect, useState, useRef } from 'react';
import './ParticleAnimation.css';

const ParticleAnimation = () => {
  // Create 10 divs, each with 100 spans (10x10 grid)
  const layers = 10;
  const gridSize = 10;
  const [colorPhase, setColorPhase] = useState(0);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // Line animation state - initialize with empty array
  const lineStateRef = useRef({
    currentPoint: { x: 0, y: 0, z: 0 },
    targetPoint: { x: 0, y: 0, z: 0 },
    progress: 0,
    speed: 0.01, // Slower speed
    trail: [], // Initialize as empty array
    maxTrailLength: 15, // Maximum number of segments in the trail
    lineColorPhase: 0 // For line color animation
  });
  
  // Color transition effect
  useEffect(() => {
    const interval = setInterval(() => {
      setColorPhase(prev => (prev + 0.005) % 2); // 0.5% change (0.005 in 0-1 scale)
      
      // Update line color phase
      lineStateRef.current.lineColorPhase = (lineStateRef.current.lineColorPhase + 0.01) % 1;
    }, 50);
    
    return () => clearInterval(interval);
  }, []);
  
  // Generate colors based on position and current color phase
  const generateColors = useMemo(() => {
    const colors = [];
    const center = gridSize / 2;
    
    for (let layer = 0; layer < layers; layer++) {
      const layerColors = [];
      
      for (let row = 0; row < gridSize; row++) {
        for (let col = 0; col < gridSize; col++) {
          // Calculate distance from center (0-1 range)
          const distanceFromCenter = Math.sqrt(
            Math.pow((row - center + 0.5) / gridSize, 2) + 
            Math.pow((col - center + 0.5) / gridSize, 2) +
            Math.pow((layer - layers/2 + 0.5) / layers, 2)
          ) / Math.sqrt(3);
          
          // Calculate color based on phase (0-1 range)
          const bluePhase = Math.sin(Math.PI * colorPhase) ** 2;
          const greenPhase = 1 - bluePhase;
          
          // Base colors
          const baseBlue = 219 * bluePhase + 50 * greenPhase;
          const baseGreen = 152 * bluePhase + 200 * greenPhase;
          const baseRed = 52 * bluePhase + 30 * greenPhase;
          
          // Adjust color based on distance
          const adjustmentFactor = 0.5;
          const blue = Math.max(20, Math.min(255, baseBlue + (0.5 - distanceFromCenter) * 100 * adjustmentFactor));
          const green = Math.max(20, Math.min(255, baseGreen + (0.5 - distanceFromCenter) * 100 * adjustmentFactor));
          const red = Math.max(20, Math.min(100, baseRed + (0.5 - distanceFromCenter) * 100 * adjustmentFactor));
          
          // Create gradient colors for sphere effect - enhanced 3D effect
          const lightColor = `rgb(${Math.min(255, red + 80)}, ${Math.min(255, green + 80)}, ${Math.min(255, blue + 80)})`;
          const midColor = `rgb(${red}, ${green}, ${blue})`;
          const darkColor = `rgb(${Math.max(0, red - 70)}, ${Math.max(0, green - 70)}, ${Math.max(0, blue - 70)})`;
          
          const gradient = `radial-gradient(circle at 25% 25%, ${lightColor}, ${midColor} 50%, ${darkColor})`;
          
          layerColors.push({
            background: gradient,
            boxShadow: `0 0 8px rgba(${red}, ${green}, ${blue}, 0.8)`,
            position: { x: col, y: row, z: layer }
          });
        }
      }
      
      colors.push(layerColors);
    }
    
    return colors;
  }, [layers, gridSize, colorPhase]);
  
  // Line animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const containerRect = canvas.parentElement.getBoundingClientRect();
    
    // Set canvas size
    canvas.width = containerRect.width;
    canvas.height = containerRect.height;
    
    // Get a random particle position within the cube
    const getRandomParticle = () => {
      const layer = Math.floor(Math.random() * layers);
      const index = Math.floor(Math.random() * gridSize * gridSize);
      const particle = generateColors[layer][index];
      return {
        x: particle.position.x,
        y: particle.position.y,
        z: particle.position.z
      };
    };
    
    // Initialize line state if empty
    const lineState = lineStateRef.current;
    
    // Always initialize the trail on first render
    if (!lineState.trail || lineState.trail.length === 0) {
      const startPoint = getRandomParticle();
      lineState.currentPoint = { ...startPoint };
      lineState.targetPoint = getRandomParticle();
      lineState.progress = 0;
      lineState.trail = [{ ...startPoint }]; // Ensure trail is initialized
    }
    
    // Transform 3D coordinates to 2D screen coordinates
    const transformPoint = (point) => {
      // Center and scale
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const scale = Math.min(canvas.width, canvas.height) * 0.4;
      
      // Apply 3D rotation from the container's animation
      const time = Date.now() * 0.001;
      const rotateX = Math.sin(time * 0.5) * 0.5;
      const rotateY = Math.cos(time * 0.5) * 0.5;
      
      // Normalize coordinates to -1 to 1 range
      const x = (point.x / gridSize) * 2 - 1;
      const y = (point.y / gridSize) * 2 - 1;
      const z = (point.z / layers) * 2 - 1;
      
      // Apply rotation
      const rotatedX = x * Math.cos(rotateY) - z * Math.sin(rotateY);
      const rotatedZ = x * Math.sin(rotateY) + z * Math.cos(rotateY);
      const rotatedY = y * Math.cos(rotateX) - rotatedZ * Math.sin(rotateX);
      
      // Project to 2D
      return {
        x: centerX + rotatedX * scale,
        y: centerY + rotatedY * scale,
        z: rotatedZ // Keep z for depth information
      };
    };
    
    // Generate a cool blue-green color based on phase
    const getCoolColor = (phase, opacity = 1) => {
      // Cyan to teal to aqua gradient
      const r = Math.floor(0 + Math.sin(phase * Math.PI) * 30);
      const g = Math.floor(180 + Math.sin(phase * Math.PI * 2) * 75);
      const b = Math.floor(200 + Math.sin(phase * Math.PI * 1.5) * 55);
      
      // Ensure values are valid
      const validR = Math.max(0, Math.min(255, r));
      const validG = Math.max(0, Math.min(255, g));
      const validB = Math.max(0, Math.min(255, b));
      const validOpacity = Math.max(0, Math.min(1, opacity));
      
      return `rgba(${validR}, ${validG}, ${validB}, ${validOpacity})`;
    };
    
    // Animation function
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update line progress
      lineState.progress += lineState.speed;
      
      // If reached target, set new target
      if (lineState.progress >= 1) {
        // Add the target point to the trail
        lineState.trail.push({ ...lineState.targetPoint });
        
        // Limit trail length
        if (lineState.trail.length > lineState.maxTrailLength) {
          lineState.trail.shift();
        }
        
        // Set current point to the target we just reached
        lineState.currentPoint = { ...lineState.targetPoint };
        
        // Get a new target point
        lineState.targetPoint = getRandomParticle();
        lineState.progress = 0;
      }
      
      // Calculate current position between current and target
      const currentPos = {
        x: lineState.currentPoint.x + (lineState.targetPoint.x - lineState.currentPoint.x) * lineState.progress,
        y: lineState.currentPoint.y + (lineState.targetPoint.y - lineState.currentPoint.y) * lineState.progress,
        z: lineState.currentPoint.z + (lineState.targetPoint.z - lineState.currentPoint.z) * lineState.progress
      };
      
      // Draw the trail (snake body)
      ctx.lineWidth = 1.5; // Thinner line
      
      // Draw the complete trail segments
      for (let i = 0; i < lineState.trail.length - 1; i++) {
        const start = transformPoint(lineState.trail[i]);
        const end = transformPoint(lineState.trail[i + 1]);
        
        // Calculate opacity based on segment position (newer segments are brighter)
        const opacity = 0.3 + (i / lineState.trail.length) * 0.6;
        
        // Get a cool color with phase offset based on segment position
        const segmentPhase = (lineState.lineColorPhase + (i / lineState.trail.length) * 0.5) % 1;
        const lineColor = getCoolColor(segmentPhase, opacity);
        
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.strokeStyle = lineColor;
        ctx.stroke();
      }
      
      // Draw the current segment (that's still moving)
      if (lineState.trail.length > 0) {
        const lastPoint = transformPoint(lineState.trail[lineState.trail.length - 1]);
        const currentPoint = transformPoint(currentPos);
        
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(currentPoint.x, currentPoint.y);
        ctx.strokeStyle = getCoolColor(lineState.lineColorPhase, 0.9);
        ctx.stroke();
        
        // Draw glow at the head
        ctx.beginPath();
        ctx.arc(currentPoint.x, currentPoint.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = getCoolColor(lineState.lineColorPhase, 1);
        ctx.fill();
        
        // Add a subtle glow effect
        try {
          const gradient = ctx.createRadialGradient(
            currentPoint.x, currentPoint.y, 0,
            currentPoint.x, currentPoint.y, 10
          );
          
          const headColor = getCoolColor(lineState.lineColorPhase, 0.8);
          const transparentColor = getCoolColor(lineState.lineColorPhase, 0);
          
          gradient.addColorStop(0, headColor);
          gradient.addColorStop(1, transparentColor);
          
          ctx.beginPath();
          ctx.arc(currentPoint.x, currentPoint.y, 10, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();
        } catch (error) {
          // Fallback to a simple circle if gradient fails
          ctx.beginPath();
          ctx.arc(currentPoint.x, currentPoint.y, 10, 0, Math.PI * 2);
          ctx.fillStyle = getCoolColor(lineState.lineColorPhase, 0.5);
          ctx.fill();
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [generateColors, gridSize, layers]);
  
  return (
    <div className="particle-animation-container">
      <div className="container">
        {[...Array(layers)].map((_, layerIndex) => (
          <div className="layer" key={layerIndex}>
            {[...Array(gridSize * gridSize)].map((_, spanIndex) => (
              <span 
                key={spanIndex}
                style={{
                  '--span-background': generateColors[layerIndex][spanIndex].background,
                  '--span-box-shadow': generateColors[layerIndex][spanIndex].boxShadow
                }}
              ></span>
            ))}
          </div>
        ))}
      </div>
      <canvas ref={canvasRef} className="particle-canvas" />
    </div>
  );
};

export default ParticleAnimation; 